/**
 * @fileoverview React Query hook for indirect cost subcategories.
 * @module features/indirect-costs/hooks/useIndirectCostSubcategories
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import {
  listIndirectCostSubcategories,
  type IndirectCostSubcategoryDTO,
  type ListIndirectCostSubcategoriesParams,
} from '../api/indirectCostSubcategoriesService'

/**
 * Options supported by the `useIndirectCostSubcategories` hook.
 */
export type UseIndirectCostSubcategoriesOptions = Pick<ListIndirectCostSubcategoriesParams, 'category' | 'search'>

/**
 * Loads indirect cost subcategories for the current organization.
 *
 * @param options Optional filter configuration (defaults to `active: true`)
 * @returns React Query result containing the filtered subcategories list
 * @example
 * ```ts
 * const { data: subcategories, isLoading } = useIndirectCostSubcategories({ category: 'OPEX' })
 * ```
 */
export function useIndirectCostSubcategories(
  options: UseIndirectCostSubcategoriesOptions = {}
): UseQueryResult<IndirectCostSubcategoryDTO[], Error> {
  const category = options.category ?? undefined
  const search = options.search ?? undefined
  return useQuery<IndirectCostSubcategoryDTO[], Error>({
    queryKey: ['indirect-costs', 'subcategories', category ?? null, search ?? null],
    queryFn: async () => {
      const params: ListIndirectCostSubcategoriesParams = { size: 200 }
      if (category) {
        params.category = category
      }
      if (search) {
        params.search = search
      }
      const page = await listIndirectCostSubcategories(params)
      return page.content
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  })
}
